import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import SectionHead from './SectionHead';
import Reveal from './Reveal';
import { useLang } from '@/context/LanguageContext';
import { scrollToId } from './LenisScroll';
import { TENURES, buildQuote, inr, dueDate, AMOUNT_MIN, AMOUNT_MAX, AMOUNT_STEP } from '@/lib/loan';

export default function RatesSection() {
  const { t, locale } = useLang();
  const [amount, setAmount] = useState(20000);
  const [tenure, setTenure] = useState(30);
  const q = buildQuote(amount, tenure);
  const pct = ((amount - AMOUNT_MIN) / (AMOUNT_MAX - AMOUNT_MIN)) * 100;

  const cta = () => {
    window.dispatchEvent(new CustomEvent('pdi-prefill', { detail: { amount, tenure_days: tenure } }));
    scrollToId('apply');
  };

  const rows = [
    { label: t.rates.interest, value: inr(q.interest), testid: 'rates-row-interest' },
    { label: t.rates.fee, value: inr(q.processing_fee), testid: 'rates-row-fee' },
    { label: t.rates.gst, value: inr(q.gst_on_fee), testid: 'rates-row-gst' },
  ];

  return (
    <section id="rates" className="relative py-24 lg:py-32" data-testid="rates-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow={t.rates.eyebrow} title={t.rates.title} sub={t.rates.sub} />

        <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:gap-14">
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-white/10 bg-panel/60 p-7 sm:p-9">
              <div className="flex items-end justify-between">
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  {t.rates.amount}
                </label>
                <p className="font-mono text-3xl font-bold text-pulse" data-testid="rates-amount-value">
                  {inr(amount)}
                </p>
              </div>
              <input
                type="range"
                data-testid="rates-amount-slider"
                min={AMOUNT_MIN}
                max={AMOUNT_MAX}
                step={AMOUNT_STEP}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="mt-5 w-full"
                style={{ '--fill': `${pct}%` }}
                aria-label="Loan amount"
              />
              <div className="mt-2 flex justify-between font-mono text-[11px] text-slate-600">
                <span>{inr(AMOUNT_MIN)}</span>
                <span>{inr(AMOUNT_MAX)}</span>
              </div>

              <label className="mt-8 block text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                {t.rates.tenure}
              </label>
              <div className="mt-3 flex flex-wrap gap-2">
                {TENURES.map((d) => (
                  <button
                    key={d}
                    data-testid={`rates-tenure-chip-${d}`}
                    onClick={() => setTenure(d)}
                    className={`rounded-full px-5 py-2 font-mono text-sm font-semibold transition-all ${
                      tenure === d
                        ? 'bg-pulse text-ink glow-pulse'
                        : 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {d} {t.rates.days}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="rounded-2xl border border-pulse/20 bg-gradient-to-b from-panel to-ink p-7 sm:p-9">
              <div className="space-y-3.5">
                {rows.map((r) => (
                  <div key={r.testid} className="flex items-center justify-between" data-testid={r.testid}>
                    <span className="text-sm text-slate-400">{r.label}</span>
                    <span className="font-mono text-sm font-semibold text-white">{r.value}</span>
                  </div>
                ))}
                <div className="my-5 h-px bg-white/10" />
                <div className="flex items-center justify-between" data-testid="rates-row-total">
                  <span className="font-display text-base font-bold text-white">{t.rates.total}</span>
                  <span className="font-mono text-2xl font-bold text-pulse">{inr(q.total_repayment)}</span>
                </div>
                <div className="flex items-center justify-between" data-testid="rates-row-due">
                  <span className="text-sm text-slate-400">{t.rates.due}</span>
                  <span className="font-mono text-sm font-semibold text-white">{dueDate(tenure, locale)}</span>
                </div>
                <div className="flex items-center justify-between" data-testid="rates-row-apr">
                  <span className="text-sm text-slate-400">{t.rates.apr}</span>
                  <span className="font-mono text-sm font-semibold text-gold">{q.apr_pct}%</span>
                </div>
              </div>

              <p className="mt-6 border-l-2 border-pulse/40 pl-4 text-xs leading-relaxed text-slate-500">
                {t.rates.note}
              </p>

              <button
                data-testid="rates-cta-button"
                onClick={cta}
                className="group mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-pulse py-3.5 font-display font-bold text-ink transition-all hover:bg-pulse-dim"
              >
                {t.rates.cta}
                <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
